/**
 * User-visible text of the request-a-quote modal in en, fr and es. `QuoteModal.tsx` picks the
 * language and passes the result to both renderers as `content`. The empty-name alert text is
 * in `Content.tsx`.
 */
import { getBrandInfo, LanguageContent } from "@ryerson/frontend.common";

/**
 * Keys of the modal's text. `tbd` is shown instead of an amount for a 0 price, for a line that
 * is inactive or unavailable, and for the subtotal when any line is either. `brandedEmail` is
 * the current brand's contact address (`getBrandInfo().email`), shown in the failure message.
 * `modalDescription` and `productsIncludedInQuote` are not rendered.
 */
export type QuoteModalContentProps = {
	back: string;
	clickQuotes: string;
	customerRefNumber: string;
	disclaimer: string;
	disclaimer2: string;
	domesticRequired: string;
	extendedPrice: string;
	itemCannotBePriced: string;
	lineItemNotes: string;
	lineLevelPO: string;
	modalDescription: string;
	price: string;
	product: string;
	productsIncludedInQuote: string;
	quantity: string;
	quoteName: string;
	removeAndReprice: string;
	requestPrice: string;
	saveAsQuote: string;
	saveQuote: string;
	subtotal: string;
	tbd: string;
	domesticToolTip: string;
	successPriceRequest: string;
	reviewOrReturn: string;
	failPriceRequest: string;
	finish: string;
	viewDetails: string;
	brandedEmail: string;
	priceRequest: string;
	cutApplied: string;
	cutFrom: string;
	processingRequest: string;
};

/**
 * Builds the modal's text per language. `accentColor` is not read. Called on every render of
 * `QuoteModal`.
 */
const QuoteModalContent = (accentColor: any): LanguageContent<QuoteModalContentProps> => ({
	en: {
		processingRequest: "Processing...",
		domesticRequired: "Domestic Requested",
		saveAsQuote: "Save as Quote",
		modalDescription:
			"By Saving your quote, you are locking in your price for 5 days or until the end of the month.",
		quoteName: "Quote Name",
		product: "Product",
		quantity: "Quantity (UOM)",
		price: "Price (UOM)",
		extendedPrice: "Extended Price",
		saveQuote: "Save Quote",
		clickQuotes: "Click 'Quotes' in the Menu to view your saved quotes",
		subtotal: "Subtotal (Estimated)",
		back: "Back",
		productsIncludedInQuote: "Products Included In the Quote",
		customerRefNumber: "Customer Reference #",
		lineItemNotes: "Line Item Notes",
		lineLevelPO: "Line Level PO",
		tbd: "TBD",
		itemCannotBePriced: "This product cannot be priced online.",
		disclaimer:
			"Certain items cannot be priced online at this time. To receive a complete and accurate quote for all your items, simply click “Request Price.” A price request will only save the items, the quantity, and units of measure.",
		disclaimer2:
			"To save a quote for all items that can be priced now, click “Remove and Reprice“ to remove the TBD items.",
		requestPrice: "Request Price",
		removeAndReprice: "Remove & Reprice",
		domesticToolTip:
			"If you need your products sourced from a specific country, please check the «domestic requested» box and a representative will follow up with you to complete your order.",
		successPriceRequest: "Thank you! Your request is being processed. ",
		reviewOrReturn:
			"In the meantime, would you like to review quote details or return to the previous page?",
		failPriceRequest:
			"Oops, something went wrong. Please retry later, or live chat for assistance, or contact ",
		finish: "Finish",
		viewDetails: "View Details",

		brandedEmail: getBrandInfo().email,
		priceRequest: "Price Request",
		cutApplied: "Cut Applied",
		cutFrom: "Cut From",
	},
	fr: {
		processingRequest: "Traitement...",
		domesticRequired: "Produit intérieur exigé",
		saveAsQuote: "Enregistrer en tant que devis",
		modalDescription:
			"En enregistrant votre devis, vous bloquez votre prix pendant 5 jours ou jusqu’à la fin du mois.",
		quoteName: "Nom du devis",
		product: "Produit",
		quantity: "Quantité (UdM)",
		price: "Precio (UDM)",
		extendedPrice: "Prix calculé",
		saveQuote: "Enregistrer le devis",
		clickQuotes: "Cliquez sur « Devis » dans le menu pour afficher vos devis sauvegardés",
		subtotal: "Sous-total (estimation)",
		back: "Retour ",
		productsIncludedInQuote: "Produits inclus dans le devis",
		customerRefNumber: "No de référence client",
		lineItemNotes: "Remarques sur le type d’article",
		lineLevelPO: "BC Niveau de l’article",
		tbd: "À DÉT.",
		itemCannotBePriced: "Ce produit ne peut pas être tarifé en ligne.",
		disclaimer:
			"Certains articles ne peuvent pas être tarifés en ligne pour le moment. Pour recevoir un devis complet et exact pour tous vos articles, il vous suffit de cliquer sur le bouton « Demander un prix ». Une demande de prix n’enregistrera que les articles, la quantité souhaitée et les unités de mesure.",
		disclaimer2:
			"Pour enregistrer un devis pour tous les articles qui peuvent être tarifés maintenant, cliquez sur « Supprimer et recalculer le prix » pour supprimer les articles À DÉT.",
		requestPrice: "Demander un prix",
		removeAndReprice: "Supprimer et recalculer le prix",
		domesticToolTip:
			"Si vous avez besoin de produits provenant d’un pays en particulier, veuillez cocher la case « Produit intérieur exigé », et un représentant communiquera avec vous pour compléter votre commande.",
		successPriceRequest: "Merci! Votre demande est en cours de traitement. ",
		reviewOrReturn:
			"Dans l’intervalle, souhaitez-vous consulter les détails du devis ou revenir à la page précédente?",
		failPriceRequest:
			"Oops, quelque chose s’est mal passé. Veuillez re-tenter plus tard, ou demander assistance sur le chat, ou contacter ",
		finish: "Finition",
		viewDetails: "Afficher les détails",

		brandedEmail: getBrandInfo().email,
		priceRequest: "Demande de prix",
		cutApplied: "Coupe appliquée",
		cutFrom: "Coupé depuis",
	},
	es: {
		processingRequest: "Procesamiento...",
		domesticRequired: "Doméstico requerido",
		saveAsQuote: "Guardar como cotización",
		modalDescription:
			"Al guardar su cotización, está fijando el precio durante 5 días o hasta fin de mes.",
		quoteName: "Nombre de cotización",
		product: "Producto",
		quantity: "Cantidad (UDM)",
		price: "Precio (UOM)",
		extendedPrice: "Precio extendido",
		saveQuote: "Guardar cotización",
		clickQuotes:
			"Dé clic en “Cotizaciones” dentro del menú para ver sus cotizaciones guardadas",
		subtotal: "Subtotal (estimado)",
		back: "Regresar",
		productsIncludedInQuote: "Productos incluidos en la cotización",
		customerRefNumber: "N° de referencia del cliente",
		lineItemNotes: "Notas de artículos de línea",
		lineLevelPO: "Pedido de nivel de línea",
		tbd: "POR DEFINIRSE",
		itemCannotBePriced: "Este producto no tiene precio en línea",
		disclaimer: `Algunos artículos no pueden cotizarse en línea en este momento. Para recibir una cotización completa y precisa de todos sus artículos, simplemente haga clic en "Solicitar precio". Una solicitud de precio solo guardará los artículos, la cantidad y las unidades de medida.`,
		disclaimer2:
			"Para guardar una cotización para todos los artículos cuyo precio se puede fijar ahora, haga clic en ",
		requestPrice: "Solicitar precio",
		removeAndReprice: "Eliminar y volver a fijar precio",
		domesticToolTip:
			"Si necesita que sus productos procedan de un país específico, marque la casilla ",
		successPriceRequest: "Muchas gracias. Su solicitud se está procesando. ",
		reviewOrReturn:
			"Mientras tanto, ¿desea revisar los detalles del presupuesto o volver a la página anterior?",
		failPriceRequest:
			"Oops, algo paso mal. Por favor re-pruebe màs tarde, o use el chat para asistencia, o contacte ",
		finish: "Acabado",
		viewDetails: "Ver detalles",

		brandedEmail: getBrandInfo().email,
		priceRequest: "Solicitud de precio",
		cutApplied: "Corte aplicado",
		cutFrom: "Cortado desde",
	},
});

export default QuoteModalContent;
